/**
 * Free-shipping celebration: a short brand-coloured confetti burst from the
 * free-shipping line the first time it becomes visible with products in the
 * cart (cart drawer or cart page). Fires once until the cart is emptied: when an
 * empty-cart state ([data-free-ship-reset]) renders, the next add bursts again.
 * Skips users
 * who prefer reduced motion, and is purely decorative (aria-hidden, no pointer
 * events).
 *
 * Contract: any element with [data-free-ship-celebrate] is a trigger. The cart
 * drawer re-renders its body after every cart change, so new trigger elements
 * are picked up with a MutationObserver and watched with an
 * IntersectionObserver (the drawer is display:none until opened).
 */
(() => {
  const FLAG = 'aq-free-ship-celebrated';
  const COLORS = ['#9E1B21', '#F7E5C6', '#C9A227', '#000000', '#E8C766'];
  const PIECES = 46;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const alreadyCelebrated = () => {
    try { return sessionStorage.getItem(FLAG) === '1'; } catch { return false; }
  };
  const markCelebrated = () => {
    try { sessionStorage.setItem(FLAG, '1'); } catch { /* storage blocked */ }
  };
  const resetCelebration = () => {
    try { sessionStorage.removeItem(FLAG); } catch { /* storage blocked */ }
  };

  const burst = (el) => {
    const rect = el.getBoundingClientRect();
    if (!rect.width) return;
    // A modal <dialog> sits in the top layer, so particles must live inside it to be seen.
    const host = el.closest('dialog') || document.body;
    const layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:2147483647;';
    host.appendChild(layer);

    const originX = rect.left + rect.width / 2;
    const originY = rect.top + rect.height / 2;
    const animations = [];

    for (let i = 0; i < PIECES; i += 1) {
      const piece = document.createElement('span');
      const size = 5 + Math.random() * 6;
      const isStrip = Math.random() > 0.5;
      piece.style.cssText = [
        'position:absolute',
        `left:${originX}px`,
        `top:${originY}px`,
        `width:${isStrip ? size * 0.5 : size}px`,
        `height:${isStrip ? size * 1.6 : size}px`,
        `background:${COLORS[i % COLORS.length]}`,
        `border-radius:${isStrip ? '1px' : '50%'}`,
        'box-shadow:0 0 0 0.5px rgba(0,0,0,0.08)',
      ].join(';');
      layer.appendChild(piece);

      const angle = (Math.PI * 2 * i) / PIECES + (Math.random() - 0.5) * 0.6;
      const power = 70 + Math.random() * 110;
      const dx = Math.cos(angle) * power;
      const dy = Math.sin(angle) * power - 60;
      const spin = (Math.random() - 0.5) * 720;

      animations.push(piece.animate([
        { transform: 'translate(-50%, -50%) scale(0.6) rotate(0deg)', opacity: 1 },
        { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(1) rotate(${spin * 0.6}deg)`, opacity: 1, offset: 0.45 },
        { transform: `translate(calc(-50% + ${dx * 1.15}px), calc(-50% + ${dy + 160}px)) scale(0.9) rotate(${spin}deg)`, opacity: 0 },
      ], {
        duration: 1300 + Math.random() * 600,
        easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)',
        fill: 'forwards',
      }).finished);
    }

    // Gentle pop on the line itself.
    el.animate([
      { transform: 'scale(1)' },
      { transform: 'scale(1.04)' },
      { transform: 'scale(1)' },
    ], { duration: 450, easing: 'ease-out' });

    Promise.allSettled(animations).then(() => layer.remove());
  };

  const visibility = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      visibility.unobserve(entry.target);
      if (alreadyCelebrated() || reduceMotion.matches) continue;
      markCelebrated();
      // Let the drawer finish its open transition before bursting.
      setTimeout(() => burst(entry.target), 250);
    }
  }, { threshold: 0.6 });

  const hasReset = (root) => Boolean(root.matches?.('[data-free-ship-reset]') || root.querySelector?.('[data-free-ship-reset]'));

  const watch = (root) => {
    // Cart emptied (empty state rendered): allow a fresh burst when products are added again.
    if (hasReset(root)) resetCelebration();
    if (alreadyCelebrated()) return;
    const found = root.matches?.('[data-free-ship-celebrate]')
      ? [root]
      : root.querySelectorAll?.('[data-free-ship-celebrate]') || [];
    for (const el of found) visibility.observe(el);
  };

  watch(document);
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1) watch(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
